/**
 * --------------------
 * COMMON
 * --------------------
 */
export enum Gender {
  M = 'MALE',
  F = 'FEMALE',
  X = 'UNSPECIFIED'
}

export type Id = number | string

export type Timestamp = number

export interface CreatedOn {
  created_on: Timestamp
  updated_on: Timestamp
}

export interface ResponseDataPaginate {
  page: number
  total_pages: number
  total_results: number
}

export interface ResponseDataError {
  exception: string
  reasons?: {
    message: string
    violated_policy?: {
      policy_expense_limits: any
    }
  }
}

/**
 * @todo review layout - remove optional props
 */
export interface Avatar {
  active?: boolean
  birthdate?: Timestamp
  gender: Gender
  included?: boolean
  name: string
  selected?: boolean
  user_id: Id
  user_visible?: boolean
  vat_number?: string
}

/**
 * @todo review layout - change updated_by to Avatar | null
 */
export interface CreatedBy {
  created_by: Avatar
  updated_by: Avatar
}

export interface PhoneContact {
  calling_code: string
  contact_id: Id
  is_whatsapp: boolean
  main_phone: boolean
  phone_number: string
}

export type Contact = PhoneContacts &
  EmailContacts & {
    contact_id: Id
    description: string
    organization_id: Id
    user_id: Id
    contact_type: 'EMAIL' | 'PHONE'
  }

export type PhoneContacts = {
  phone_contacts: PhoneContact[]
}

export type EmailContact = {
  contact_id: Id
  email: string
  main_email: boolean
  verified: boolean
}

export interface EmailContacts {
  email_contacts: EmailContact[]
}

export type Contacts = Contact[]

export interface AddressCity {
  city_id: Id
  name: string
}

export interface AddressState {
  state_id: Id
  name: string
  abbreviation: string
}

export interface AddressCountry {
  country_id: Id
  name: string
  abbreviation: string
  calling_code: string
  iso: string
}

export interface Address {
  address_id: Id
  city: AddressCity
  city_id: Id
  state: AddressState
  state_id: Id
  country: AddressCountry
  country_id: Id
  address: string
  address_number: string
  description: string
  zip_code: string
}

/**
 * --------------------
 * USER
 * --------------------
 */
export enum Roles {
  Approver = 'approver',
  Checker = 'checker',
  Controller = 'controller',
  Manager = 'manager',
  Quotator = 'quotator',
  Traveler = 'traveler'
}

export enum UserLevel {
  User = 'USER',
  Admin = 'ADMIN',
  SuperAdmin = 'SUPER_ADMIN',
  RealmAdmin = 'REALM_ADMIN'
}

export enum UserType {
  User = 'USER',
  Guest = 'GUEST'
}

export interface RoleDescription {
  assign_tree_id: Id
  assign_tree_name: string
}

export type RoleGroup = { [K in Roles]: RoleDescription[] }

export interface BasicUser {
  active: boolean
  agree_terms: boolean
  birthdate: Timestamp
  code: string
  contacts: Contacts
  driver_license_expires_on: Timestamp
  driver_license_number: number
  first_name: string
  gender: Gender
  integrator?: boolean // logged in user
  last_name: string
  login: string
  name?: string
  organization_id: Id
  password: string
  position: string
  preferred_language: 'EN' | 'ES' | 'PT'
  requester: boolean
  auditor: boolean
  role_group: RoleGroup
  tenant_id: Id
  theme: 'DARK' | 'DEFAULT'
  user_id: Id
  user_level_type: UserLevel
  user_type: UserType
}

export enum RoleType {
  GUEST = 'GUEST',
  APPROVER = 'APPROVER',
  CHECKER = 'CHECKER',
  CONTROLLER = 'CONTROLLER',
  QUOTATOR = 'QUOTATOR',
  ISSUER = 'ISSUER',
  MANAGER = 'MANAGER'
}

export type RoleGroupTrees = {
  active?: boolean
  assign_tree_id: Id
  role_type: RoleType
}

export interface User extends BasicUser {
  address: Address | null
  auditor: boolean
  contract_id: Id
  contract: Contract | null
  cost_center_id: Id
  cost_center: CostCenter | null
  dashboard: boolean
  in_toodopay?: boolean
  master_key?: string
  must_change_password: boolean
  passport_expires_on: Timestamp | null
  passport_number: string | null
  photo: string | null
  reference_id: Id | null
  reference: Reference | null
  role_group_trees: RoleGroupTrees[]
  timezone: string | null
  user_trees: BasicUserTree[]
  vat_number: string
}

/**
 * --------------------
 * ORGANIZATION
 * --------------------
 */
export enum OrganizationType {
  HOLDING = 'HOLDING',
  SUBSIDIARY = 'SUBSIDIARY'
}

export interface BasicOrganization {
  active: boolean
  address: Address | null
  code: string
  contacts: Contacts
  contract: Contract
  current_users: number
  legal_name: string
  organization_id: Id
  organization_type: OrganizationType
  preferred_language: string
  tenant: Tenant
  trade_name: string
  user_limit: number
  vat_number: string
}

export interface Organization extends BasicOrganization, CreatedBy, CreatedOn {}

export type Tenant = {
  organizations: Organization[]
  tenant_id: Id
  tenant_identity: string
  tenant_name: string
  tenant_type: 'COMPANY' | 'AGENCY'
}

/**
 * --------------------
 * Budgets
 * --------------------
 */

export type BudgetChartTicketStandardType =
  | ServiceType
  | 'AIR'
  | 'HOTEL'
  | 'VEHICLE'

export interface BudgetChartBudgets {
  action: PolicyAction
  budget_diff: number
  budget_id: Id
  budget_violated: boolean
  current_issued_budget: number
  current_reserved_budget: number
  free_budget: number
  issued_budget: number
  key?: number
  limit_amount: number
  reserved_budget: number
  title: string
}

export interface BudgetChartBudget {
  budget_id?: Id
  cost_center_id: Id
  share?: number
}

export interface BudgetChartTicket {
  request_ticket_id?: Id
  service_id?: Id
  standard_type: BudgetChartTicketStandardType
  total_value: number
}

export interface BudgetAllocationsCheck {
  active: boolean
  budgets: BudgetChartBudget[]
  organization_id: Id
  policy_rule_id: Id
  tickets: BudgetChartTicket[]
}

export enum BudgetItemType {
  ADVANCE = 'ADVANCE',
  EXPENSE = 'EXPENSE',
  GENERAL = 'GENERAL',
  SERVICE = 'SERVICE'
}

export enum BudgetReferenceType {
  ANUAL = 'QUANTITY',
  MONTHLY = 'MONTHLY'
}

export enum BudgetType {
  INDIVIDUAL = 'INDIVIDUAL',
  MAIN = 'MAIN',
  SHARED = 'SHARED'
}

export interface Budget {
  active: string
  budget_id: number
  budget_item_id: number
  budget_reference_type: BudgetReferenceType
  budget_type: BudgetType
  cost_center_id: Id
  description: string
  limit_amount: number
  reference_date: number
  share: number
  title: string
}

/**
 * --------------------
 * RESOURCE
 * --------------------
 */
export interface BasicResource {
  active: boolean
  code: string
  description: string
  name: string
  organization_id: Id
  organization: Organization
  standard: boolean
}

export interface BillingType extends BasicResource {
  billing_type_id: Id
  locked: boolean
  shared_with: number[]
}

export interface BudgetItem extends BasicResource {
  budget_item_id: Id
  budget_item_type: BudgetItemType
}

export interface Contract extends BasicResource {
  contract_id: Id
  shared_with: number[]
}

export interface CostCenter extends BasicResource {
  budgets: Budget[]
  cost_center_id: Id
  shared_with: number[]
}

export interface Currency
  extends Pick<BasicResource, 'active' | 'name' | 'standard'> {
  code: string
  currency_id: Id
  decimal_digits: number
}

export interface FinancialResource extends BasicResource {
  financial_resource_id: Id
  shared_with: number[]
}

export interface PaymentResource extends BasicResource {
  corporate: boolean
  payment_resource_id: Id
  refundable: boolean
  shared_with: number[]
  toodopay: boolean
  locked: boolean
}

export interface Reason extends BasicResource {
  reason_id: Id
}

export interface Reference extends BasicResource {
  reference_id: Id
  shared_with: number[]
}

export enum ServiceType {
  QUANTITY = 'QUANTITY',
  VALUE = 'VALUE'
}

export enum StandardType {
  AIR = 'AIR',
  BUS = 'BUS',
  CAR = 'CAR',
  HOTEL = 'HOTEL',
  KM = 'KM',
  REGULAR_SERVICE = 'REGULAR_SERVICE',
  TRAVEL_INSURANCE = 'TRAVEL_INSURANCE',
  UBER = 'UBER'
}

export interface Service extends BasicResource {
  budget_item_id: Id
  budget_item: BudgetItem
  locked: boolean
  service_id: Id
  service_type: ServiceType
  standard_type: StandardType
}

export interface Target extends BasicResource {
  target_id: Id
  shared_with: number[]
}

/**
 * --------------------
 * REQUEST
 * --------------------
 */
export type RequestUserDataStatus =
  | 'APPROVAL_PENDING'
  | 'APPROVAL_REPROVED'
  | 'CANCELED'
  | 'UNDERWAY'
  | 'CONFERENCE_PENDING'
  | 'CONFERENCE_REPROVED'
  | 'CLOSING_PENDING'
  | 'CLOSING_REPROVED'
  | 'FINISHED'

export type RequestTicketType =
  | 'ADVANCE'
  | 'AIR'
  | 'CHECK_IN' // need confirm
  | 'EXPENSE'
  | 'HOTEL'
  | 'OTHER'
  | 'TRACK'
  | 'VEHICLE'

export enum ConsensusSituation {
  CONSENSUS_PENDING = 'CONSENSUS_PENDING',
  IN_CONSENSUS = 'IN_CONSENSUS',
  REGULAR_APPROVAL = 'REGULAR_APPROVAL',
  REGULAR_CLOSING = 'REGULAR_CLOSING',
  REGULAR_CONFERENCE = 'REGULAR_CONFERENCE'
}
export interface RequestUserData {
  allowed?: boolean
  approval?: boolean
  closing?: boolean
  conference?: boolean
  consensus_situation?: ConsensusSituation
  cost_center: CostCenter
  created_on?: number
  included: boolean
  reason_id: Id
  reason?: Reason
  request_approvals?: RequestApproval[]
  request_closings?: RequestClosing[]
  request_conferences?: RequestConference[]
  request_emission?: RequestEmission
  request_id?: Id
  request_nps?: Nps
  request_user_data_id?: Id
  status?: RequestUserDataStatus
  traveler?: Avatar
  updated_on?: number
  user_id?: Id
}

export type Nps = {
  request_nps_id: Id
  request_id: Id
  request_user_data_id: Id
  organization_id: Id
  request_nps_number: Id
  active: boolean
  message: string
  last_execution: number
  rate: number
  status: 'UNDERWAY'
  nps_type: 'GENERAL'
}

interface RequestApprovalRecord {
  approved_by: string
  approver: Avatar
  created_on: number
  justification: string
  request_approval_id: number
  request_approval_record_id: number
  status: RequestApprovalStatus
  updated_on: number
}

interface RequestConferenceRecord {
  checker: Avatar
  conferred_by: string
  created_on: number
  justification: string
  request_conference_id: number
  request_conference_record_id: number
  status: RequestConferenceStatus
  updated_on: number
}

interface RequestClosingRecord {
  closed_by: string
  controller: Avatar
  created_on: number
  justification: string
  request_closing_id: number
  request_closing_record_id: number
  status: RequestClosingStatus
  updated_on: number
}

interface RequestEmissionRecord {
  created_on: number
  issued_by: string
  issuer: Avatar
  justification: string
  request_emission_id: number
  request_emission_record_id: number
  status: RequestClosingStatus
  updated_on: number
}

export type RequestApproval = {
  request_approval_id: Id
  request_approval_records: RequestApprovalRecord[]
  request_id: Id
  situation: 'ACTIVE'
  status: RequestApprovalStatus
}

export type RequestConference = {
  request_conference_id: Id
  request_conference_records: RequestConferenceRecord[]
  request_id: Id
  situation: 'ACTIVE'
  status: RequestConferenceStatus
}

export type RequestClosing = {
  request_closing_id: Id
  request_closing_records: RequestClosingRecord[]
  request_id: Id
  situation: 'ACTIVE'
  status: RequestClosingStatus
}

export type RequestEmission = {
  request_emission_id: Id
  request_emission_records: RequestEmissionRecord[]
  request_id: Id
  situation: string
  status: RequestEmissionStatus
}

export type StatusRequestItineraryCheckIn =
  | 'accomplished'
  | 'expired'
  | 'outside'
  | 'pending'

export interface RequestItineraryCheckIn {
  activity: string
  checkin_on: Timestamp
  latitude: string
  longitude: string
  request_itinerary_checkin_id: Id
  request_itinerary_id: Id
  status?: StatusRequestItineraryCheckIn
}

export interface RequestItinerary {
  active: boolean
  activity: string
  description: string
  ended_on: Timestamp
  latitude: string
  longitude: string
  reference?: Reference
  reference_id: Id
  request_itinerary_id: Id
  request_itinerary_number: Id
  started_on: Timestamp
  tmp_id?: Id
  request_id: Id
  request_itinerary_checkins?: RequestItineraryCheckIn[]
  status?: StatusRequestItineraryCheckIn[]
  travelers?: Avatar[]
}

export interface SaveRequestStandardAssignments {
  request_standard_billings?: RequestStandardBilling[]
  request_standard_sharings?: RequestStandardSharing[]
}

interface BasicStandardAssignment {
  active: boolean
  request_id: Id
  tmp_id?: Id
}

export interface RequestStandardSharing extends BasicStandardAssignment {
  contract?: Contract
  contract_id: Id
  cost_center?: CostCenter
  cost_center_id: Id
  percentage: number
  request_standard_sharing_id: Id
  request_standard_sharing_number: Id
  request_ticket_sharing_type: 'UNIFIED'
}

export type BillingReferenceType = 'CONTRACT' | 'COST_CENTER'

export interface RequestStandardBilling extends BasicStandardAssignment {
  billing_reference_type: BillingReferenceType
  billing_type?: BillingType
  billing_type_id: Id
  observation: string
  request_standard_billing_id: Id
  request_standard_billing_number: Id
  target?: Target
  target_id: Id
}

export interface RequestStandardAssignments {
  request_standard_sharings: RequestStandardSharing[]
  request_standard_billings: RequestStandardBilling[]
}

export interface RequestTicketUserData {
  request_ticket_user_data_id: Id
  request_ticket_id: Id
  included: boolean
  owner: boolean
  traveler?: Avatar
  user_id?: Id
}

export enum RequestTicketTitleAdvanceSituation {
  ACTIVE = 'ACTIVE',
  APPROVED = 'APPROVED',
  APPROVING = 'APPROVING',
  CANCELED = 'CANCELED',
  EXPIRED = 'EXPIRED',
  ISSUED = 'ISSUED',
  QUOTATION = 'QUOTATION',
  RESERVED = 'RESERVED',
  WAITING = 'WAITING',
  WAITING_RESERVATION = 'WAITING_RESERVATION'
}
export interface RequestTicketTitleAdvance {
  active: boolean
  amount: number
  cost: number
  currency_id: Id
  currency?: Currency
  exchange_rate: number
  financial_resource_id: Id
  financial_resource?: FinancialResource
  request_ticket_title_advance_id: Id
  request_ticket_title_advance_number: Id
  situation: RequestTicketTitleAdvanceSituation
}

export interface RequestTicketReceiptImage {
  active: boolean
  image_url: string
  request_ticket_receipt_image_id: Id
  request_ticket_receipt_image_number: Id
  tmp_id?: Id
}

export interface RequestTicketReceiptTrack {
  trip_distance: number
  started_on: number
  time_spent: number
  ended_on: number
}

export interface RequestTicketReceipt {
  active: boolean
  amount: number
  billed: boolean
  checked_receipt: boolean // used in the conference
  cost: number
  currency_id: Id
  currency?: Currency
  date: Timestamp
  exchange_rate: number
  gas_value: number
  invoice: string
  justification: string
  payment_resource_id: Id
  payment_resource?: PaymentResource
  policy_violated: boolean
  quantity: number
  reference_id: Id
  reference?: Reference
  request_ticket_receipt_id: Id
  request_ticket_receipt_images: RequestTicketReceiptImage[]
  request_ticket_receipt_number: Id
  request_ticket_receipt_tracks: RequestTicketReceiptTrack[]
  service_id: Id
  service?: Service
}

export enum RequestTicketClassType {
  EXPENSE = 'EXPENSE',
  SERVICE = 'SERVICE',
  TITLE = 'TITLE'
}

interface RequestViolatedJustification {
  request_justification_id: Id
  request_id: Id
  user_id: Id
  policy_request_justification: {
    policy_request_justification_id: Id
    organization_id: Id
    justification_type: string
    code: string
    name: string
    description: string
    custom: string
    locked: string
    active: string
    standard: string
  }
}

export interface RequestTicket {
  active: boolean
  class_type: RequestTicketClassType
  cost: number
  is_toodopay: boolean
  merchant_name?: string
  receiver_id?: any
  request_id: Id
  request_ticket_air_selected?: RequestTicketAirSelected
  request_ticket_hotel_selected?: RequestTicketHotelSelected
  request_ticket_id: Id
  request_ticket_number: Id
  request_ticket_receipts: RequestTicketReceipt[]
  request_ticket_title_advances: RequestTicketTitleAdvance[]
  request_ticket_type: RequestTicketType
  request_ticket_user_data: RequestTicketUserData[]
  request_ticket_vehicle_selected?: RequestTicketVehicleSelected
  request_violated_justification?: RequestViolatedJustification
  status: RequestTicketStatus
  sync_status: RequestTicketStatusSync
  tenant_id: Id
  tmp_id?: Id
  user_id: Id
}

export type RequestTicketStatus =
  | 'APPROVAL_PENDING'
  | 'APPROVAL_REPROVED'
  | 'CANCELED'
  | 'CLOSING_PENDING'
  | 'CLOSING_REPROVED'
  | 'CONFERENCE_PENDING'
  | 'CONFERENCE_REPROVED'
  | 'EMISSION_CANCELED'
  | 'EMISSION_PENDING'
  | 'FINISHED'
  | 'ISSUED'
  | 'QUOTATION_PENDING'

export enum RequestTicketSelectedSituation {
  APPROVING = 'APPROVING',
  CANCELED = 'CANCELED',
  EMISSION_CANCELED = 'EMISSION_CANCELED',
  EXPIRED = 'EXPIRED',
  ISSUED = 'ISSUED',
  QUOTATION = 'QUOTATION',
  RESERVED = 'RESERVED',
  WAITING_RESERVATION = 'WAITING_RESERVATION'
}

export type RequestTicketAirSelected = {
  // air_policy: "1"
  active: boolean
  checkin_date: Timestamp
  checkout_date: Timestamp
  comments: string
  eticket_code: string
  fee_value: number
  organization_id: Id
  query_date: Timestamp
  request_quote_air_trip: RequestQuoteAirTrip
  request_ticket_air_selected_id: Id
  request_ticket_air_selected_number: Id
  request_ticket_id: Id
  situation: RequestTicketSelectedSituation
}

export type RequestQuoteAirTrip = {
  boarding_value: number
  booking_code: string
  booking_expiration: Timestamp
  fare_value: number
  fee_value: Id
  organization_id: Id
  request_id: Id
  request_quote_air_one_way: RequestQuoteAir
  request_quote_air_round_trip: RequestQuoteAir
  request_quote_air_trip_id: Id
  request_quote_air_trip_number: Id
  service_value: number
  total_air_value: number
}

export type RequestTicketVehicleSelected = {
  request_ticket_vehicle_selected_id: Id
  organization_id: Id
  request_ticket_id: Id
  request_quote_vehicle: RequestQuoteVehicle
  driver: boolean
  // "vehicle_policy": null,
  comments: string
  booking_code: string
  booking_expiration: Timestamp
  eticket_code: string
  situation: RequestTicketSelectedSituation
}

export type RequestTicketHotelSelected = {
  request_ticket_hotel_selected_id: Id
  organization_id: Id
  request_ticket_id: Id
  request_quote_hotel_room: RequestQuoteHotelRoom & {
    request_quote_hotel: Omit<RequestQuoteHotel, 'request_quote_hotel_rooms'>
  }
  request_ticket_hotel_selected_number: Id
  room_number: number
  // hotel_policy: null
  comments: string
  booking_code: string
  booking_expiration: Timestamp
  situation: RequestTicketSelectedSituation
  eticket_code: string
}

export interface RequestCustomData {
  active: boolean
  contract?: Contract
  contract_id: Id
  request_custom_data_id: Id
  request_id: Id
  target?: Target
  target_id: Id
  tenant_id: Id
  tmp_id?: Id
}

export interface RequestEmpoweredUsers
  extends Pick<
    BasicUser,
    'active' | 'code' | 'gender' | 'requester' | 'user_id'
  > {
  name: string
  role: keyof RoleGroup
  traveler: boolean
}

export interface BasicRequest {
  contract_id: Id
  request_custom_data: RequestCustomData[]
  request_type: 'TRIP' | 'EXPENSE'
  request_user_data: RequestUserData[]
  requested_on: Timestamp
  title: string
  tmp_reason_id?: Id
  tmp_id?: Id
}

export enum RequestTicketStatusSync {
  END_SYNC = 'END_SYNC',
  NOT_SYNC = 'NOT_SYNC',
  SYNC = 'SYNC'
}

export interface Request extends BasicRequest {
  allowed?: boolean // informs if you can show the buttons on approval, conference and closing
  approval?: boolean // tells whether the logged user can approve
  cost: number
  date_end: number
  empowered_users?: RequestEmpoweredUsers[] // empowered_users is used in the request listing, 'myrequest'
  erp_code?: string
  full_request_air: FullRequestAir[]
  full_request_hotel: FullRequestHotel[]
  full_request_vehicle: FullRequestVehicle[]
  justification: string
  obs: string
  organization_id: Id
  request_base_id?: number
  request_id: Id
  request_itineraries: RequestItinerary[]
  request_number: Id
  request_standard_assignments: RequestStandardAssignments
  request_tickets: RequestTicket[]
  requested_on: number
  requester: Avatar
  status: RequestUserDataStatus
  ticket_types: RequestTicketType[]
}

export type ResumeVehicle = {
  agreement_id: Id
  air_conditioning: boolean
  chosen: boolean
  company_image_url: string
  company: string
  currency_id: Id
  fare_value: number
  fee_value: number
  gearbox: string
  information: string
  message_id: string
  min_vehicle: boolean
  model: string

  request_quote_hash: string
  request_quote_route_hash: string
  request_quote_vehicle_hash: string
  request_quote_vehicle_unique: string
  return_date_original: string
  return_date: Timestamp
  return_location: string
  service_value: number
  total_value: number
  values: {
    customer_supplier_id: Id
    message_id: string
    request_quote_hash: string
    request_quote_vehicle_unique: string
    total_value: number
  }[]
  vehicle_image_url: string
  vehicle_type: string
  withdrawal_date_original: string
  withdrawal_date: Timestamp
  withdrawal_location: string
}

export enum IntegrationType {
  OFFLINE = 'OFFLINE',
  ONLINE = 'ONLINE'
}

export type ResumeHotel = {
  latitude: string
  longitude: string
  quarter: string
  checkin_date: Timestamp
  checkout_date: Timestamp
  brand_name: string
  address: string
  integration_type: IntegrationType
  hotel_image_url: string
  category_stars: number
  request_quote_hotel_hash: string
  request_quote_hotel_group_hash: string
  request_quote_hotel_rooms: ResumeHotelRequestQuoteHotelRooms[]
}

export type ResumeHotelRequestQuoteHotelRooms = {
  customer_supplier_id: Id
  currency_id: Id
  category: string
  integration_type: IntegrationType
  pension: boolean
  max_adults: number
  selected: boolean
  fare_value: number
  fee_value: number
  tax_value: number
  total_value: number
  min_room: boolean
  request_quote_hotel_room_hash: string
  request_quote_hotel_hash: string
  request_quote_hotel_group_hash: string
  request_quote_hash: string
  request_quote_route_hash: string
  message_id: string
  request_quote_hotel_unique: string
}

export type ResumeAir = {
  route: string
  request_quote_airs: ResumeAirRequestQuoteAirs[]
}

export type ResumeAirRequestQuoteAirs = {
  agreement_id: Id
  air_destiny: string
  air_origin: string
  arrival_date: Timestamp
  company: AirlinesCodes
  currency_id: Id
  departure_date: Timestamp
  flight_code: string
  flight_time: number
  journey_time: number
  request_quote_air_fares: ResumeAirRequestQuoteAirFares[]
  request_quote_air_flights: RequestQuoteAirFlight[]
  request_quote_air_group_hash: string
  request_quote_air_hash: string
  request_quote_hash: string
  stops: number
  transaction_id: Id
}

export type ResumeAirRequestQuoteAirFares = {
  baggage_amount: number
  baggage_weight: number
  baggage: boolean
  boarding_value: number
  customer_supplier_id: Id
  family: string
  fare_value: number
  fee_value: number
  key: string
  message_id: string
  min_fare: boolean
  request_quote_air_group_hash: string
  request_quote_air_hash: string
  request_quote_air_route_hash: string
  request_quote_fare_hash: string
  request_quote_hash: string
  selected: boolean
  service_value: number
  total_value: number
}

export type Agency = {
  active: boolean
  agency_id: Id
  agency_organization: Pick<
    BasicOrganization,
    'address' | 'code' | 'contacts' | 'tenant' | 'trade_name' | 'vat_number'
  >
  agency_type: 'REGULAR'
  name: string
  organization_id: Id
}

export type Supplier = {
  active: boolean
  agency: Agency
  code: string
  country: Country
  customer_name: string
  organization_id: Id
  status: 'APPROVED'
  supplier_id: Id
}

export type RequestQuoteServiceCustomerSupplier = {
  customer_supplier_id: Id
  supplier: Supplier
}

export type RequestQuoteService = RequestQuote & {
  customer_supplier_id: Id
  customer_supplier: RequestQuoteServiceCustomerSupplier
  request_quote_id: Id
  request_quote_service_type: RequestTicketType
  status: RequestApprovalStatus
}

export type FullRequestVehiclesHistory = {
  request_quote_vehicle_routes: RequestQuoteHotelRoute[]
  request_quotes: RequestQuote[]
}

export type FullRequestVehicle = {
  customer_supplier_id?: Id
  justification?: string[]
  message_id: string[]
  request_quote_hash: string
  request_quote_vehicle_hash: string[]
  request_quote_vehicle_route_hash: string
  request_quote_vehicle_unique: string[]
  situation: RequestTicketSelectedSituation
  travelers: {
    owner: boolean
    user_id: Id
  }[]
}

export type RequestQuoteVehicleRoute = {
  chosen: boolean
  query_date: Timestamp
  request_quote_vehicles: RequestQuoteVehicle[]
  return_date: Timestamp
  return_location: 'CITY' | 'AIRPORT'
  withdrawal_date: Timestamp
  withdrawal_location: 'CITY' | 'AIRPORT'
}

export type RequestQuoteVehicleTraveler = { owner: boolean; user_id: Id }

export type RequestQuoteVehicle = {
  air_conditioning: boolean
  chosen: boolean
  company_image_url: string
  company: string
  fare_value: number
  fee_value: number
  gearbox: string
  information: string
  min_vehicle: boolean
  model: string
  request_quote_hash: string
  request_quote_vehicle_hash: Id
  request_quote_vehicle_unique: Id
  request_quote: RequestQuoteService
  return_date: Timestamp
  return_location: string
  service_value: number
  total_value: number
  travelers?: RequestQuoteVehicleTraveler[]
  vehicle_image_url: string
  vehicle_type: string
  withdrawal_date: Timestamp
  withdrawal_location: string
}

export type GroupedAirQuotes = {
  air_destiny: string
  air_origin: string
  arrival_date: string
  company: AirlinesCodes
  departure_date: string
  flight_time: number
  identifier: Id
  journey_time: number
  request_quote_air_fares: RequestQuoteAirFare[]
  request_quote_air_flights: RequestQuoteAirFlight[]
  stops: number
}

export type GroupedAir = {
  air_destiny: string
  air_origin: string
  journey: number
  quote_air_groups: GroupedAirQuotes[]
}

export type FullRequestAirHistory = {
  request_quote_air_routes: RequestQuoteAirRoute[]
  request_quotes: RequestQuote[]
}

export type FullRequestAir = {
  customer_supplier_id?: Id
  justification?: string[]
  message_id: string[]
  request_quote_hash: string
  request_quote_air_route_hash: string
  request_quote_air_hash: string[]
  request_quote_air_fare_hash: string[]
}

export type Car = {
  amount: number
  extra: string[]
  id: Id
  image?: string
  rental_company: 'localiza' | 'movida'
  title: string
}

export type RequestApprovalSituation =
  | 'ACTIVE'
  | 'APPROVED'
  | 'CANCELED'
  | 'WAITING'

export type RequestApprovalStatus =
  | 'APPROVED'
  | 'CANCELED'
  | 'EXPIRED'
  | 'PENDING'
  | 'REPROVED'

export type RequestClosingSituation =
  | 'ACTIVE'
  | 'APPROVED'
  | 'CANCELED'
  | 'WAITING'

export type RequestClosingStatus =
  | 'APPROVED'
  | 'CANCELED'
  | 'EXPIRED'
  | 'PENDING'
  | 'REPROVED'

export type RequestConferenceSituation =
  | 'ACTIVE'
  | 'APPROVED'
  | 'CANCELED'
  | 'WAITING'

export type RequestConferenceStatus =
  | 'APPROVED'
  | 'CANCELED'
  | 'EXPIRED'
  | 'PENDING'
  | 'REPROVED'

export type RequestEmissionStatus =
  | 'ACTIVE'
  | 'APPROVED'
  | 'CANCELED'
  | 'WAITING'

export type OccurrenceType = 'APPROVAL' | 'EMISSION' | 'CONFERENCE' | 'CLOSING'

export interface RequestHistoryOccurrence {
  created_on: number
  justification?: string
  occurrence_type: OccurrenceType
  responsible: {
    gender: Gender
    name: string
  }
  status: RequestApprovalStatus | RequestUserDataStatus
  user_id: Id
}

export interface RequestHistory {
  created_on: number
  occurrences: RequestHistoryOccurrence[]
  status: RequestUserDataStatus
  traveler: Avatar
  updated_on: number
  user_id: Id
}

/**
 * --------------------
 * POLICIES
 * --------------------
 */
export enum PolicyAction {
  ALERT = 'ALERT',
  BLOCK = 'BLOCK'
}

export enum PolicyOperator {
  LESS_THAN = '<',
  LESS_THAN_OR_EQUAL_TO = '<=',
  EQUALS = '==',
  GREATER_THAN_OR_EQUAL_TO = '>=',
  GREATER_THAN = '>'
}

export interface PolicyExpenseLimits {
  action: PolicyAction
  active: boolean
  currency_id: Id
  currency?: Currency
  daily_limit: number
  limit: number
  operator: PolicyOperator
  policy_expense_limit_id: Id
  policy_id: number
  reason_id: Id
  reason?: Reason
  reference_id: Id
  reference?: Reference
  service_id: Id
  service?: Service
  value: number
}

export interface PolicyRequestBudgeRule {
  action: PolicyAction
  active: boolean
  policy_request_budget_rule_id: Id
  policy_request_budget_rule_prefix: string
  policy_rule_id: Id
  priority: boolean
  validate_budget: boolean
  value: number
}

export interface PolicyRequestGeneralRule {
  action: PolicyAction
  active: boolean
  description: string
  minimum_days_limit: boolean
  operator: PolicyOperator
  policy_request_general_rule_id: Id
  policy_request_rule_prefix: string
  policy_rule_id: Id
  service_by_customer_supplier: boolean
  value: number
}

export interface PolicyReceiptRule {
  action: PolicyAction
  active: boolean
  any_value: boolean
  currency_id: Id
  currency?: Currency
  description: string
  operator: PolicyOperator
  policy_id: number
  policy_receipt_rule_id: Id
  policy_receipt_rule_prefix: string
  reason_id: Id
  reason?: Reason
  reference_id: Id
  reference?: Reference
  service_id: Id
  service?: Service
  value: number
}

export interface PolicyRequestAirRule {
  action: PolicyAction
  active: boolean
  description: string
  lowest_fare: boolean
  operator: PolicyOperator
  policy_request_air_rule_id: Id
  policy_request_air_rule_prefix: string
  policy_rule_id: Id
  value: number
}
export interface PolicyRequestHotelRule {
  action: string
  active: boolean
  description: string
  lowest_fare: boolean
  operator: PolicyOperator
  policy_request_hotel_rule_id: Id
  policy_request_hotel_rule_prefix: string
  policy_rule_id: Id
  value: number
}

export interface PolicyRequestVehicleRule {
  action: string
  active: boolean
  description: string
  lowest_fare: boolean
  operator: PolicyOperator
  policy_request_vehicle_rule_id: Id
  policy_request_vehicle_rule_prefix: number
  policy_rule_id: Id
  value: Id
}

export interface PolicyRule {
  active: boolean
  description: string
  disable_advance_retroactive: boolean
  enable_advance: boolean
  enable_billing_observation: boolean
  enable_budget: boolean
  enable_bus: boolean
  enable_check_receipt: boolean
  enable_checkin: boolean
  enable_def_custom_contract: boolean
  enable_def_custom_target: boolean
  enable_enforce_additional_info: boolean
  enable_flight: boolean
  enable_hotel: boolean
  enable_itinerary: boolean
  enable_nps?: boolean
  enable_qr_code_check_diferent_vat: boolean
  enable_qr_code_check_vat: boolean
  enable_qr_code?: boolean
  enable_receipt_antifraud: boolean
  enable_resume: boolean
  enable_standard_billing: boolean
  enable_standard_sharing: boolean
  enable_track: boolean
  enable_vehicle_rent: boolean
  force_image: boolean
  forward_to_expense: boolean
  name: string
  policy_id: Id
  policy_receipt_rules?: PolicyReceiptRule[]
  policy_request_air_rule?: PolicyRequestAirRule
  policy_request_budget_rule?: PolicyRequestBudgeRule
  policy_request_general_rule?: PolicyRequestGeneralRule
  policy_request_hotel_rule?: PolicyRequestHotelRule
  policy_request_vehicle_rule?: PolicyRequestVehicleRule
  policy_rule_id: Id
  qrcode_retroative_date: boolean
  qrcode_show_antifraud: boolean
  reason_id: Id
  reason?: Reason
}

export interface Policy {
  active: boolean
  description: string
  name: string
  organization_id: Id
  policy_expense_limits: PolicyExpenseLimits[]
  policy_id: Id
  policy_rules: PolicyRule[]
}

export type Country = {
  abbreviation: string
  calling_code: string
  country_id: Id
  iso: string
  name: string
  reference_currency_id: Id
}

export type State = {
  country: Country
  country_id: Id
  name: string
  state_id: Id
}

export type City = {
  city_id: Id
  city_name: string
  state_name: string
  trend_city_id: Id
}

export type Airport = {
  iata_code: string
  name: string
}

export type RequestQuote = {
  agent_tree_id: Id
  request_quote_type: IntegrationType
  expire_date: Timestamp
  chosen: boolean
  request_quote_hash: string
}

export type RequestQuoteAirRouteItinerary = 'ONE_WAY' | 'ROUND_TRIP'

export type RequestQuoteAirRoute = {
  origin: string
  destiny: string
  itinerary: RequestQuoteAirRouteItinerary
  departure_date: Timestamp
  arrival_date: Timestamp
  interval: number
  chosen: boolean
  request_quote_air_groups: RequestQuoteAirGroup[]
}

export type RequestQuoteAirGroup = {
  identifier: Id
  journey: number
  request_quote_airs: RequestQuoteAir[]
}

export type RequestQuoteAir = {
  air_destiny: string
  air_origin: string
  arrival_date: Timestamp
  chosen?: boolean
  company: AirlinesCodes
  currency_id: Id
  currency: Currency
  customer_agreement_id: Id
  departure_date: Timestamp
  flight_time: number
  journey_time: number
  request_quote: Omit<RequestQuoteService, 'request_quote_hash'>
  request_quote_air_fares: RequestQuoteAirFare[]
  request_quote_air_flights: RequestQuoteAirFlight[]
  request_quote_air_group_hash: []
  request_quote_air_id?: Id
  request_quote_hash: string
  stops: number
}

export type RequestQuoteAirFare = {
  baggage_amount: number
  baggage_weight: number
  baggage: boolean
  boarding_value: number
  family: string
  fare_value: number
  fee_value: number
  key: Id
  min_fare: boolean
  request_quote_fare_hash: Id
  request_quote_hash: string
  selected: boolean
  service_value: number
  total_value: number
}

export type RequestQuoteAirFlight = {
  aircraft: number
  arrival_date: Timestamp
  company: AirlinesCodes
  connection: number
  departure_date: Timestamp
  duration: number
  flight_code: string
  flight_destiny: string
  flight_origin: string
}

export type AirlinesCodes =
  | 'AQ'
  | 'GB'
  | 'X7'
  | 'A3'
  | 'EI'
  | 'JK'
  | 'NIG'
  | '4A'
  | 'SU'
  | 'AR'
  | '3S'
  | 'VW'
  | 'AM'
  | '5D'
  | '6R'
  | 'VIZ'
  | 'J7'
  | '8U'
  | 'ZB'
  | 'AH'
  | '6I'
  | 'G9'
  | 'KC'
  | 'CC'
  | 'UU'
  | 'BT'
  | 'KF'
  | 'BP'
  | 'BUR'
  | '2J'
  | 'BX'
  | 'SM'
  | 'TY'
  | 'AC'
  | 'RV'
  | 'TX'
  | '2Q'
  | '9H'
  | '3C'
  | 'CA'
  | 'CAO'
  | '3E'
  | 'XK'
  | 'HF'
  | 'YN'
  | 'HD'
  | 'EN'
  | 'UX'
  | 'AF'
  | 'ZX'
  | 'GL'
  | 'GT'
  | 'AHO'
  | 'LD'
  | 'HT'
  | 'NY'
  | 'AI'
  | 'IX'
  | '3H'
  | 'IG'
  | 'K7'
  | 'JS'
  | 'AL'
  | '7Q'
  | 'NX'
  | 'MD'
  | 'KM'
  | 'ZM'
  | 'MK'
  | 'MV'
  | '9U'
  | 'SW'
  | 'NZ'
  | 'PX'
  | '4N'
  | 'BFF'
  | '7P'
  | 'P4'
  | 'PJ'
  | 'HC'
  | 'RS'
  | 'HM'
  | 'VT'
  | 'TN'
  | 'TC'
  | 'TS'
  | '8C'
  | '3N'
  | 'NF'
  | 'AXY'
  | 'UM'
  | '9T'
  | 'AK'
  | 'D7'
  | 'PA'
  | 'RU'
  | 'AIB'
  | '4Y'
  | 'SB'
  | 'AEG'
  | 'ED'
  | '4Z'
  | 'USC'
  | 'TL'
  | 'APC'
  | 'JU'
  | 'ASP'
  | 'T6'
  | '9L'
  | 'NWG'
  | 'PNX'
  | 'AS'
  | 'KO'
  | 'AP'
  | '2B'
  | 'AZ'
  | 'NH'
  | 'G4'
  | '9I'
  | 'TTX'
  | 'QQ'
  | '4W'
  | 'UJ'
  | 'KH'
  | '5A'
  | 'HP'
  | 'Z8'
  | 'AMV'
  | 'AA'
  | 'A8'
  | 'M6'
  | 'GEO'
  | 'DM'
  | 'OY'
  | '2G'
  | 'ADB'
  | 'GP'
  | 'AZE'
  | 'FG'
  | 'W3'
  | 'IZ'
  | 'AG'
  | 'OZ'
  | 'KP'
  | '3V'
  | '5O'
  | 'FAH'
  | 'ABR'
  | 'I3'
  | 'RC'
  | '5Y'
  | 'KK'
  | 'V8'
  | 'LSK'
  | 'GR'
  | 'HZ'
  | 'AU'
  | 'OS'
  | 'ATV'
  | 'YK'
  | 'ZR'
  | 'AV'
  | 'QT'
  | '4B'
  | 'X9'
  | '9V'
  | 'J2'
  | 'A4'
  | 'S4'
  | 'AD'
  | 'ZF'
  | 'QU'
  | 'ELH'
  | 'BND'
  | 'RHD'
  | 'DFL'
  | 'UP'
  | 'QH'
  | 'PG'
  | '5B'
  | 'ID'
  | 'OD'
  | 'JV'
  | 'Z9'
  | 'B2'
  | 'CH'
  | '8E'
  | 'BYA'
  | '8H'
  | 'B3'
  | 'BG'
  | '3B'
  | 'NT'
  | '0B'
  | 'BZ'
  | 'SI'
  | 'BV'
  | 'BBG'
  | 'BO'
  | 'BOE'
  | 'OB'
  | 'TF'
  | 'BAY'
  | 'BHL'
  | 'BTZ'
  | 'L9'
  | 'BA'
  | 'SN'
  | 'U4'
  | 'BFL'
  | 'BKV'
  | 'LB'
  | 'FB'
  | '1T'
  | 'UZ'
  | 'JMP'
  | 'VR'
  | '5C'
  | 'A7'
  | 'MO'
  | 'CMD'
  | 'QC'
  | 'KR'
  | 'K6'
  | '5T'
  | 'PM'
  | '9K'
  | 'EGL'
  | 'JD'
  | '8F'
  | 'CGF'
  | 'W8'
  | 'P3'
  | 'CV'
  | 'C8'
  | 'BW'
  | 'V3'
  | 'IV'
  | 'CSJ'
  | 'KA'
  | 'CX'
  | 'CVK'
  | 'KX'
  | '5J'
  | 'C2'
  | '5Z'
  | '9M'
  | 'GM'
  | 'CE'
  | '6Q'
  | 'HRT'
  | 'HEM'
  | 'HNL'
  | 'HKS'
  | 'EU'
  | 'CI'
  | 'CK'
  | 'MU'
  | 'G5'
  | 'CF'
  | 'CZ'
  | 'KN'
  | 'PN'
  | 'OQ'
  | 'NDL'
  | 'QG'
  | 'WX'
  | 'GY'
  | 'MN'
  | 'CS'
  | 'MLM'
  | 'FGD'
  | 'DE'
  | 'LF'
  | 'V0'
  | 'CM'
  | 'CAT'
  | 'XC'
  | 'SS'
  | 'Z7'
  | 'OU'
  | 'CU'
  | 'RGN'
  | 'CY'
  | 'OK'
  | 'CCD'
  | '9J'
  | 'DX'
  | 'DCS'
  | 'DF'
  | 'DL'
  | 'DPJ'
  | 'D0'
  | '3R'
  | 'DZ'
  | 'R6'
  | 'KB'
  | 'DUB'
  | 'FEI'
  | 'ZE'
  | 'T3'
  | 'VE'
  | 'U2'
  | '8J'
  | 'WK'
  | 'MS'
  | 'MSX'
  | 'LY'
  | 'EL'
  | 'EMB'
  | 'EK'
  | 'E4'
  | 'B8'
  | 'EG'
  | 'ES'
  | 'ET'
  | 'EY'
  | 'YU'
  | 'EW'
  | 'BR'
  | 'E9'
  | '5V'
  | 'XSR'
  | 'EJM'
  | 'XN'
  | '7A'
  | 'MG'
  | 'FE'
  | 'FX'
  | 'FJ'
  | 'AY'
  | 'FY'
  | '7F'
  | 'FRN'
  | 'F8'
  | 'FRF'
  | 'W2'
  | 'LXJ'
  | 'FCK'
  | 'OPT'
  | 'FTL'
  | '8W'
  | 'IF'
  | '4V'
  | 'OJ'
  | '5F'
  | '5H'
  | 'F3'
  | 'BE'
  | 'FO'
  | '6W'
  | 'DW'
  | 'FZ'
  | 'FT'
  | 'VP'
  | 'XY'
  | 'FP'
  | 'FH'
  | 'FRG'
  | 'BF'
  | 'F9'
  | 'JH'
  | 'FU'
  | 'GAJ'
  | 'GMA'
  | 'GA'
  | '4G'
  | 'A9'
  | 'G8'
  | '6G'
  | 'G3'
  | 'Y5'
  | 'G6'
  | 'YR'
  | 'CN'
  | 'ZK'
  | 'ZG'
  | 'GF'
  | 'GX'
  | 'HR'
  | 'HU'
  | 'HGR'
  | 'HA'
  | 'NS'
  | 'HHE'
  | 'HSO'
  | '2L'
  | '5K'
  | 'H9'
  | 'OI'
  | 'HX'
  | 'UO'
  | 'HTM'
  | 'MR'
  | '0Q'
  | 'I4'
  | 'II'
  | 'IB'
  | 'I2'
  | 'FW'
  | 'FI'
  | '6E'
  | 'JY'
  | '4O'
  | 'IO'
  | 'IR'
  | 'B9'
  | 'EP'
  | 'IA'
  | '6H'
  | 'I8'
  | 'XM'
  | 'JM'
  | '3X'
  | 'JL'
  | 'NU'
  | 'J9'
  | 'QD'
  | '7C'
  | 'JF'
  | 'J4'
  | 'LS'
  | 'B6'
  | 'JA'
  | 'JQ'
  | 'RSP'
  | 'WU'
  | 'RY'
  | 'LJ'
  | 'R5'
  | 'ENZ'
  | 'XE'
  | 'HO'
  | '8K'
  | 'KAY'
  | 'K4'
  | 'K9'
  | 'RQ'
  | 'NV'
  | 'FK'
  | 'M5'
  | 'KQ'
  | 'KG'
  | 'KW'
  | 'KHO'
  | 'Y9'
  | 'KL'
  | 'KE'
  | 'KY'
  | 'KU'
  | 'B0'
  | 'TM'
  | 'LQ'
  | 'QV'
  | 'LK'
  | 'QL'
  | 'LA'
  | 'JJ'
  | 'UC'
  | 'OE'
  | 'VK'
  | 'LI'
  | 'LN'
  | 'YL'
  | 'JT'
  | 'LM'
  | 'LT'
  | 'GJ'
  | 'LO'
  | '8L'
  | 'LH'
  | 'GEC'
  | 'LTR'
  | 'LW'
  | 'LG'
  | 'W5'
  | '3W'
  | 'MH'
  | 'Q2'
  | 'AE'
  | 'JE'
  | '7Y'
  | 'NR'
  | '7M'
  | 'MP'
  | 'M7'
  | 'MY'
  | 'L6'
  | 'MAX'
  | 'SUM'
  | 'ME'
  | 'VL'
  | 'JI'
  | 'YV'
  | 'LL'
  | 'OM'
  | '8G'
  | 'MWT'
  | 'IM'
  | 'MJF'
  | 'MB'
  | 'MW'
  | 'QM'
  | 'YM'
  | 'MAL'
  | 'M9'
  | '8M'
  | 'UB'
  | 'MJ'
  | 'IN'
  | 'N8'
  | 'JTE'
  | 'ON'
  | 'NO'
  | 'RA'
  | 'NA'
  | 'NE'
  | 'NJE'
  | '1I'
  | 'EJ'
  | 'E3'
  | 'EXT'
  | 'NP'
  | 'KZ'
  | 'DD'
  | 'XW'
  | 'N5'
  | 'NHX'
  | 'Y7'
  | 'N4'
  | 'NCB'
  | 'NFA'
  | 'NC'
  | 'NHC'
  | 'J3'
  | 'DY'
  | 'BJ'
  | 'O9'
  | 'N9'
  | 'VQ'
  | 'BK'
  | 'OA'
  | 'OLY'
  | 'WY'
  | '8Q'
  | '6O'
  | 'PUL'
  | 'OXF'
  | '8P'
  | 'PK'
  | 'PB'
  | '8Y'
  | 'ZP'
  | 'P6'
  | '2Z'
  | 'MM'
  | 'EO'
  | 'PC'
  | 'PFY'
  | 'KS'
  | 'PE'
  | 'YP'
  | 'P9'
  | 'PR'
  | 'CNS'
  | 'PU'
  | 'CG'
  | 'DP'
  | 'PO'
  | 'LPR'
  | 'OL'
  | 'PD'
  | 'M4'
  | 'PW'
  | 'PWA'
  | 'PVG'
  | 'QF'
  | 'QLK'
  | 'QR'
  | 'QB'
  | 'QW'
  | 'LBQ'
  | 'MTL'
  | '7H'
  | 'TH'
  | 'WZ'
  | 'RX'
  | 'ZL'
  | 'EE'
  | 'YX'
  | 'FV'
  | 'RG'
  | 'AT'
  | 'BI'
  | 'RFJ'
  | 'RL'
  | 'RJ'
  | 'DR'
  | '7R'
  | '5R'
  | 'WB'
  | '7S'
  | 'FR'
  | 'RAC'
  | 'S7'
  | 'XZ'
  | 'FA'
  | '4Q'
  | 'OV'
  | 'RZ'
  | 'SK'
  | 'SP'
  | '9R'
  | 'SV'
  | '6S'
  | 'DV'
  | 'TR'
  | 'BB'
  | 'IS'
  | 'ER'
  | 'WV'
  | 'D2'
  | 'O3'
  | 'SC'
  | 'FM'
  | 'ZH'
  | '3U'
  | 'P8'
  | '7L'
  | 'MI'
  | '3M'
  | 'SQ'
  | 'SQC'
  | 'HMF'
  | 'H2'
  | 'ZA'
  | 'Q7'
  | 'GQ'
  | 'GG'
  | 'IOS'
  | 'M8'
  | 'SLQ'
  | 'BC'
  | 'SYB'
  | 'TE'
  | 'QN'
  | 'PQ'
  | 'OO'
  | '5N'
  | '6Y'
  | 'QS'
  | '6J'
  | 'SOP'
  | 'SZ'
  | 'SOR'
  | 'S8'
  | 'SA'
  | '9S'
  | 'WN'
  | 'SG'
  | 'NK'
  | '9C'
  | 'IJ'
  | 'UL'
  | 'SJ'
  | 'PV'
  | 'DJ'
  | '7G'
  | 'Q4'
  | 'RE'
  | 'NUK'
  | 'FFD'
  | 'SD'
  | '9X'
  | 'SY'
  | 'EZ'
  | 'SR'
  | 'XQ'
  | 'CNK'
  | 'WG'
  | 'Y8'
  | 'URF'
  | 'PY'
  | 'WQ'
  | 'WT'
  | 'MDF'
  | 'LX'
  | 'SAZ'
  | 'WO'
  | 'AWU'
  | 'RB'
  | 'TW'
  | 'DT'
  | 'HH'
  | 'TI'
  | 'EQ'
  | 'TP'
  | 'NI'
  | 'YQ'
  | '3T'
  | 'RO'
  | 'SF'
  | 'U9'
  | 'X5'
  | 'TG'
  | 'SL'
  | 'WE'
  | 'VZ'
  | 'MT'
  | 'GS'
  | 'TV'
  | 'TT'
  | 'IT'
  | 'ZT'
  | 'TFX'
  | 'C3'
  | 'TJ'
  | 'Q8'
  | 'TGY'
  | 'TMW'
  | 'AX'
  | 'HV'
  | 'KTB'
  | '8B'
  | '4T'
  | 'IL'
  | '2T'
  | 'X3'
  | 'TU'
  | 'UG'
  | 'TK'
  | 'T5'
  | 'TRK'
  | 'T9'
  | 'U8'
  | 'T7'
  | 'UKL'
  | 'PS'
  | 'GO'
  | 'UE'
  | 'B7'
  | 'UW'
  | 'UA'
  | 'UNO'
  | '5X'
  | 'UD'
  | 'U6'
  | 'UQ'
  | 'BS'
  | 'JUS'
  | 'UT'
  | 'RT'
  | 'HY'
  | 'V9'
  | 'JW'
  | '0V'
  | 'VNE'
  | 'VC'
  | 'V4'
  | 'VJ'
  | 'VN'
  | 'VS'
  | 'VA'
  | 'V2'
  | 'VJT'
  | 'UK'
  | 'VH'
  | 'VB'
  | 'Y4'
  | 'VI'
  | 'V7'
  | 'VY'
  | 'EB'
  | 'WSG'
  | 'WH'
  | 'SWN'
  | 'NPT'
  | 'KD'
  | 'WS'
  | 'WR'
  | 'WI'
  | 'WF'
  | 'WIG'
  | 'WHS'
  | 'WM'
  | '7W'
  | 'P5'
  | 'IW'
  | 'W7'
  | 'W6'
  | 'WL'
  | 'MF'
  | 'XOJ'
  | 'XP'
  | 'R3'
  | 'YC'
  | 'YE'
  | 'IY'
  | 'YT'
  | 'YG'
  | 'ZO'
  | 'ORZ'

export type RequestAgencyStatus =
  | 'CANCELED'
  | 'EMISSION_PENDING'
  | 'EMISSION_REPROVED'
  | 'FINISHED'
  | 'QUOTATION_PENDING'
  | 'QUOTATION_REPROVED'

export type FullRequestHotelsHistory = {
  request_quote_hotel_routes: RequestQuoteHotelRoute[]
  request_quotes: RequestQuote[]
}

export type FullRequestHotelRooms = {
  request_quote_hash: string
  request_quote_hotel_route_hash: string
  request_quote_hotel_unique: string
  request_quote_hotel_room_hash: string
  guests: {
    owner: boolean
    user_id: Id
  }[]
}

export type FullRequestHotel = {
  customer_supplier_id?: Id
  integration_type: IntegrationType
  justification?: string[]
  message_id: string[]
  request_quote_hotel_hash: string
  rooms: FullRequestHotelRooms[]
}

export type RequestQuoteHotelRoute = {
  address: string
  checkin_date: Timestamp
  checkout_date: Timestamp
  chosen: boolean
  description: string
  longitude: string
  query_date: Timestamp
  request_quote_hotel_groups: RequestQuoteHotelGroup[]
  request_quote_vehicles?: RequestQuoteVehicle[]
}

export type RequestQuoteHotelGroup = {
  identifier: Id
  request_quote_hotels: RequestQuoteHotel[]
}

export type RequestQuoteHotel = {
  address: string
  agreement_id: Id
  best_value: number
  brand_name: string
  category_stars: number
  checkin_date: Timestamp
  checkout_date: Timestamp
  chosen: boolean
  hotel_image_url: string
  latitude: string
  laundry: boolean
  longitude: string
  nights: number
  parking: boolean
  pool: boolean
  quarter: string
  request_quote_agreement_id: Id
  request_quote_hash: string
  request_quote_hotel_hash: Id
  request_quote_hotel_rooms: RequestQuoteHotelRoom[]
  request_quote: Omit<RequestQuoteService, 'request_quote_hash'>
  restaurant: boolean
}

export interface QuarterFilter {
  checked: boolean
  qtd: number
  title: string
}

export type RequestQuoteHotelRoom = {
  category: string
  currency_id: Id
  fare_value: number
  fee_value: number
  guests?: RequestQuoteHotelRoomGuest[]
  integration_type: IntegrationType
  min_room: boolean
  pension: boolean
  request_quote_hotel_room_hash?: Id
  request_quote_hotel_room_id?: Id
  selected: boolean
  tax_value: number
  total_value: number
}

export type RequestQuoteHotelRoomGuest = { owner: boolean; user_id: Id }

export type Approver = {
  approved_by: ApprovedBy
  approver: {
    active: boolean
    gender: Gender
    name: string
    requester: boolean
    user_id: Id
  }
  status: RequestApprovalStatus
}

export type Checker = {
  checker: {
    active: boolean
    gender: Gender
    name: string
    requester: boolean
    user_id: Id
  }
  conferred_by: CheckedBy
  status: RequestConferenceStatus
}
export type Controller = {
  closed_by: ControlledBy
  controller: {
    active: boolean
    gender: Gender
    name: string
    requester: boolean
    user_id: Id
  }
  status: RequestClosingStatus
}

export type ApprovedBy = 'APPROVER' | 'AUTO' | 'POLICY'
export type CheckedBy = 'CHECKER' | 'AUTO' | 'POLICY'
export type ControlledBy = 'CLOSER' | 'AUTO' | 'POLICY'

export enum PAssignTreeType {
  COST_CENTER = 'COST_CENTER',
  ORGANIZATION = 'ORGANIZATION',
  REASON = 'REASON',
  TENANT = 'TENANT',
  USER = 'USER'
}

type UserGroupTrees = {
  assign_tree_id: Id
  user_id: Id
  tenant_id: Id
  user: User[]
}

export type BasicUserTree = {
  parent_id: Id
  user_level_group_trees: {
    active?: boolean
    user_level_type: UserLevel
  }[]
  active?: boolean
}

interface UserTree extends BasicUserTree {
  assign_tree_id: Id
  assign_tree_name: string
  heritable_properties: boolean
  tenant_id: Id
  p_assign_tree_type: PAssignTreeType
  code: string
  user_group_trees: UserGroupTrees[]
}

export interface Place {
  label: string
  value: string
}

interface BasicAssignTree {
  assign_tree_id: Id
  assign_tree_name: string
  chained: boolean
  childrens: AssignTree[]
  code: string
  color?: string
  disabled?: boolean
  heritable_properties: boolean
  parent_id: Id
  policies: []
  tenant_id: Id
}

interface TenantAssignTree extends BasicAssignTree {
  p_assign_tree_type: PAssignTreeType.TENANT
}

interface OrganizationAssignTree extends BasicAssignTree {
  organization_id: Id
  p_assign_tree_type: PAssignTreeType.ORGANIZATION
}

interface ReasonAssignTree extends BasicAssignTree {
  p_assign_tree_type: PAssignTreeType.REASON
  reason_id: Id
}

interface CostCenterAssignTree extends BasicAssignTree {
  cost_center_id: Id
  p_assign_tree_type: PAssignTreeType.COST_CENTER
}

interface UserAssignTree extends BasicAssignTree {
  p_assign_tree_type: PAssignTreeType.USER
  user_id: Id
}

export type AssignTree =
  | CostCenterAssignTree
  | OrganizationAssignTree
  | ReasonAssignTree
  | TenantAssignTree
  | UserAssignTree

export type Asset = {
  assets_tenant_id: Id
  tenant_id: Id
  organization: Id
  assets_type: 'VIDEO'
  description: string
  value: string
}

export type PolicyFlowApproval = {
  active: boolean
  automatic_approval: boolean
  code: string
  consensual_approval: boolean
  cost_center_approval: boolean
  description: string
  exception_approver: boolean
  name: string
  policy_approval_id: Id
  reason_approval: boolean
}

export type PolicyFlowConference = {
  active: boolean
  automatic_conference: boolean
  code: string
  consensual_conference: boolean
  cost_center_conference: boolean
  description: string
  exception_checker: boolean
  name: string
  policy_conference_id: Id
  reason_conference: boolean
}

export type PolicyFlowClosing = {
  active: boolean
  automatic_closing: boolean
  code: string
  consensual_closing: boolean
  cost_center_closing: boolean
  description: string
  name: string
  policy_closing_id: Id
}

export enum PolicyFlowFormat {
  STANDARD = 'STANDARD',
  AUTOMATIC = 'AUTOMATIC',
  CONSENSUS = 'CONSENSUS'
}

export enum PolicyFlowType {
  HIERARCHY = 'HIERARCHY',
  COST_CENTER = 'COST_CENTER',
  REASON = 'REASON'
}

export interface PolicyAntiFraud {
  active: boolean
  description: string
  organization_id: Id
  organization?: Pick<Organization, 'trade_name'>
  policy_request_receipt_antifraud_id: Id
  policy_rule_id: Id
  policy_rule?: Pick<PolicyRule, 'name'>
  quantity: number
  value: number
  vat: boolean
}

export interface PolicyTree {
  assign_tree_id: Id
  policy_approval_id: Id
  policy_approval: PolicyFlowApproval
  policy_closing_id: Id
  policy_closing: PolicyFlowClosing
  policy_conference_id: Id
  policy_conference: PolicyFlowConference
  policy_id: Id
  policy: Policy
}

interface PolicyExceptionTrees {
  assign_tree_id: Id
  tree_exception_tree_name: string
  remove?: true
}

export interface PolicyApprovalExceptionTrees extends PolicyExceptionTrees {
  policy_approval_rule_id: Id
}

export interface PolicyApprovalRules {
  active: boolean
  code: string
  include_original_approvers: boolean
  name: string
  organization_id: Id
  policy_approval_exception_trees: PolicyApprovalExceptionTrees[]
  policy_approval_id: Id
  policy_approval_rule_id: Id
  policy_request_air_rule_id: Id
  policy_request_general_rule_id: Id
  policy_request_hotel_rule_id: Id
  policy_request_vehicle_rule_id: Id
}

export interface PolicyConferenceExceptionTrees extends PolicyExceptionTrees {
  policy_conference_rule_id: Id
}

export interface PolicyConferenceRules {
  active: boolean
  code: string
  include_original_checkers: boolean
  name: string
  organization_id: Id
  policy_conference_exception_trees: PolicyConferenceExceptionTrees[]
  policy_conference_id: Id
  policy_conference_rule_id: Id
  policy_receipt_rule_id: Id
}

export interface UserEmailNotification {
  active: boolean
  attach_travel_report: boolean
  email: string
  notification_status: RequestUserDataStatus
  organization_id: Id
  reason_id: Id
  reason?: Reason
  user_email_notification_id: Id
  validate_reason: boolean
}

export interface AgreementServiceHub {
  name: string
  service_hub_id: Id
  service_hub_type: string
}

export interface Agreement {
  access_code: string
  active: boolean
  agreement_id: Id
  agreement_type: string
  branch_id: string
  code: string
  domain: string
  fee_value: number
  gtw_agency_id: string
  gtw_group_id: string
  integration_type: string
  name: string
  organization_id: Id
  organization: Organization
  package_group: string
  password: string
  service_hub_id: Id
  service_hub: AgreementServiceHub
  username: string
}

export type RequestToClassify = Pick<
  Request,
  | 'date_end'
  | 'justification'
  | 'obs'
  | 'organization_id'
  | 'request_number'
  | 'request_user_data'
  | 'requested_on'
  | 'title'
> &
  Pick<CostCenter, 'cost_center_id'> & {
    cost_center_code: string
    cost_center_name: string
    costCenter?: CostCenter
    reason_code: string
    reason_id: string
    reason_name: string
    reason?: Reason
    contract?: Contract
    request_base_id: number
    user_id: number
    user?: User
  }
